import React from "react";
import Tile from "../../types/tile";
import {
  ChartsGrid,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
  ResponsiveChartContainer,
} from "@mui/x-charts";
import theme from "../../theme";
import { useSelector } from "../../store";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";

interface Props {
  tileModel: Tile;
  detail?: boolean;
}

const LineTile: React.FC<Props> = (props) => {
  const { tileModel, detail } = props;

  const { tileDetailStatus } = useSelector((store) => store.tiles);

  const namesArray = tileModel.data?.map((item) => item.name);
  const valuesArray = tileModel.data?.map((item) => +item.data[0]);

  const md = useMediaQuery(theme.breakpoints.up("md"));
  const lg = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{ width: "100%", height: "100%" }}
      justifyContent="center"
      position="absolute"
      top={{ xs: "-35px", md: "-30px" }}
      paddingX={{ xs: 0, md: "5px" }}
      paddingBottom={{
        xs: detail ? "28vw" : "50px",
        sm: detail ? "30vw" : "80px",
        md: detail ? "40vw" : "110px",
        lg: "150px",
        xl: "170px",
      }}
    >
      {!!detail &&
      tileDetailStatus === "loading" &&
      (!tileModel.data || tileModel.data.length! < 1) ? (
        <CircularProgress />
      ) : (
        <ResponsiveChartContainer
          sx={{
            marginLeft: 3,
            "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
              fill: "#fff !important",
            },
            "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
              fill: "#fff !important",
            },
            "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
              stroke: "#fff !important",
            },
            "& .MuiChartsAxis-left .MuiChartsAxis-line": {
              stroke: "#fff !important",
            },
            "& .MuiChartsAxis-label": {
              fill: "#fff !important",
              "& text": {
                fontSize: lg
                  ? "18px !important"
                  : md
                  ? "15px !important"
                  : "12px !important",
              },
            },
            "& .MuiChartsAxis-tick": {
              stroke: "#fff !important",
            },
          }}
          series={[
            {
              data: valuesArray,
              type: "line",
              label: "",
              valueFormatter: (v, ctx) => {
                const name = namesArray![ctx.dataIndex];
                return `${name} : ${v}`;
              },
            },
          ]}
          xAxis={[
            {
              data: namesArray,
              scaleType: "band",
              valueFormatter: md
                ? namesArray?.length! < 6
                  ? (value) => `${value.slice(0, 5)}`
                  : namesArray?.length! < 10
                  ? (value) => `${value.slice(0, 2)}`
                  : () => ``
                : () => ``,
            },
          ]}
          yAxis={[
            {
              colorMap: {
                type: "piecewise",
                thresholds: [0, tileModel?.threshold! + 1],
                colors: [
                  theme.palette.tertiary.main,
                  theme.palette.success.main,
                  theme.palette.error.main,
                ],
              },
            },
          ]}
        >
          <LinePlot skipAnimation={detail} />
          <MarkPlot />
          <ChartsGrid vertical horizontal />
          <ChartsXAxis
            label={
              tileModel.xAxisDataType?.value
                ? tileModel.xAxisIncrement?.value
                  ? `${tileModel.xAxisDataType?.value} (${tileModel.xAxisIncrement?.value})`
                  : `${tileModel.xAxisDataType?.value}`
                : "Time (Hour of Day)"
            }
            position="bottom"
          />
          <ChartsYAxis
            label={md ? tileModel.yAxisDataType?.value || "Count" : ""}
          />
          <ChartsTooltip trigger="item" />
        </ResponsiveChartContainer>
      )}
    </Box>
  );
};

export default LineTile;
